import { BLOCK } from './blocks.js';
import type { Expr, Program, Stmt, ValueType } from './ir.js';

/**
 * Program IR → Blockly workspace JSON: the inverse of `parse.ts`, so a student who typed their
 * program as text can switch back to blocks (ADR 0012). The shape matches what
 * `Blockly.serialization.workspaces.save` produces, which is what the parser reads back.
 */

interface BlockJson {
  type: string;
  id: string;
  fields?: Record<string, unknown>;
  inputs?: Record<string, { block: BlockJson }>;
  next?: { block: BlockJson };
  extraState?: Record<string, unknown>;
}

export interface WorkspaceJson extends Record<string, unknown> {
  blocks: { languageVersion: 0; blocks: BlockJson[] };
  variables: { name: string; id: string }[];
}

const ARITH_FIELD: Record<string, string> = {
  '+': 'ADD',
  '-': 'MINUS',
  '*': 'MULTIPLY',
  '/': 'DIVIDE',
};

const COMPARE_FIELD: Record<string, string> = {
  '==': 'EQ',
  '!=': 'NEQ',
  '<': 'LT',
  '<=': 'LTE',
  '>': 'GT',
  '>=': 'GTE',
};

const READ_FIELD = { int: 'INT', double: 'DOUBLE', text: 'TEXT' } as const;

const ELEM_FIELD = { int: 'INT', double: 'DOUBLE', string: 'TEXT', bool: 'BOOL' } as const;

const varId = (name: string) => `var_${name}`;

function block(
  type: string,
  id: string,
  fields?: Record<string, unknown>,
  inputs?: Record<string, BlockJson | undefined>,
  extraState?: Record<string, unknown>,
): BlockJson {
  const b: BlockJson = { type, id };
  if (fields) b.fields = fields;
  if (inputs) {
    const entries = Object.entries(inputs).filter((e): e is [string, BlockJson] => !!e[1]);
    if (entries.length > 0)
      b.inputs = Object.fromEntries(entries.map(([k, v]) => [k, { block: v }]));
  }
  if (extraState) b.extraState = extraState;
  return b;
}

/** A chain of statements linked through `next`, the way blocks stack. */
function chain(
  stmts: readonly Stmt[],
  value: (e: Expr) => BlockJson | undefined,
): BlockJson | undefined {
  let first: BlockJson | undefined;
  let prev: BlockJson | undefined;
  for (const s of stmts) {
    const b = statement(s, value);
    if (prev) prev.next = { block: b };
    else first = b;
    prev = b;
  }
  return first;
}

function expression(e: Expr, types: Readonly<Record<string, ValueType>>): BlockJson | undefined {
  const x = (sub: Expr) => expression(sub, types);
  switch (e.k) {
    case 'missing':
      // An empty socket is an absent input, exactly as Blockly saves it.
      return undefined;
    case 'num':
      return block('math_number', e.id, { NUM: e.value });
    case 'text':
      return block('text', e.id, { TEXT: e.value });
    case 'bool':
      return block('logic_boolean', e.id, { BOOL: e.value ? 'TRUE' : 'FALSE' });
    case 'var':
      return block('variables_get', e.id, { VAR: { id: varId(e.name) } });
    case 'read':
      return block(BLOCK.read, e.id, { AS: READ_FIELD[e.as] });
    case 'arith':
      if (e.op === 'idiv') {
        return block(BLOCK.intDiv, e.id, undefined, {
          A: x(e.left),
          B: x(e.right),
        });
      }
      if (e.op === 'mod') {
        return block(BLOCK.mod, e.id, undefined, { A: x(e.left), B: x(e.right) });
      }
      return block(
        BLOCK.arith,
        e.id,
        { OP: ARITH_FIELD[e.op] },
        {
          A: x(e.left),
          B: x(e.right),
        },
      );
    case 'compare':
      return block(
        'logic_compare',
        e.id,
        { OP: COMPARE_FIELD[e.op] },
        {
          A: x(e.left),
          B: x(e.right),
        },
      );
    case 'logic':
      return block(
        'logic_operation',
        e.id,
        { OP: e.op === 'and' ? 'AND' : 'OR' },
        {
          A: x(e.left),
          B: x(e.right),
        },
      );
    case 'not':
      return block('logic_negate', e.id, undefined, { BOOL: x(e.arg) });
    case 'join':
      return block(BLOCK.join, e.id, undefined, { A: x(e.left), B: x(e.right) });
    case 'length':
      return block('text_length', e.id, undefined, { VALUE: x(e.arg) });
    case 'code':
      return block(BLOCK.code, e.id, undefined, { VALUE: x(e.arg) });
    case 'chr':
      return block(BLOCK.fromCode, e.id, undefined, { VALUE: x(e.arg) });
    case 'case':
      return block(
        BLOCK.changeCase,
        e.id,
        { CASE: e.upper ? 'UPPER' : 'LOWER' },
        { VALUE: x(e.arg) },
      );
    case 'find':
      return block(BLOCK.find, e.id, undefined, { NEEDLE: x(e.needle), TEXT: x(e.hay) });
    case 'substr':
      return block(BLOCK.substr, e.id, undefined, {
        TEXT: x(e.arg),
        FROM: x(e.from),
        LEN: x(e.len),
      });
    case 'at':
      if (types[e.name] === 'string') {
        return block(BLOCK.charAt, e.id, { VAR: { id: varId(e.name) } }, { INDEX: x(e.index) });
      }
      return e.index2
        ? block(
            BLOCK.atTable,
            e.id,
            { VAR: { id: varId(e.name) } },
            {
              INDEX: x(e.index),
              INDEX2: x(e.index2),
            },
          )
        : block(BLOCK.at, e.id, { VAR: { id: varId(e.name) } }, { INDEX: x(e.index) });
    case 'size':
      return block(BLOCK.size, e.id, {
        VAR: { id: varId(e.name) },
        DIM: e.dim === 2 ? 'TWO' : 'ONE',
      });
  }
}

function statement(s: Stmt, value: (e: Expr) => BlockJson | undefined): BlockJson {
  switch (s.k) {
    case 'set':
      return block(
        'variables_set',
        s.id,
        { VAR: { id: varId(s.name) } },
        { VALUE: value(s.value) },
      );
    case 'change':
      return block('math_change', s.id, { VAR: { id: varId(s.name) } }, { DELTA: value(s.delta) });
    case 'print':
      // A line of its own is the plain print block; the other endings are the inline one.
      return s.end === 'newline'
        ? block(BLOCK.print, s.id, undefined, { VALUE: value(s.value) })
        : block(
            BLOCK.printInline,
            s.id,
            { END: s.end === 'space' ? 'SPACE' : 'NONE' },
            {
              VALUE: value(s.value),
            },
          );
    case 'if': {
      const inputs: Record<string, BlockJson | undefined> = {};
      s.branches.forEach((b, i) => {
        inputs[`IF${i}`] = value(b.cond);
        inputs[`DO${i}`] = chain(b.body, value);
      });
      if (s.orElse) inputs['ELSE'] = chain(s.orElse, value);
      const extra: Record<string, unknown> = {};
      if (s.branches.length > 1) extra['elseIfCount'] = s.branches.length - 1;
      if (s.orElse) extra['hasElse'] = true;
      return block(
        'controls_if',
        s.id,
        undefined,
        inputs,
        Object.keys(extra).length > 0 ? extra : undefined,
      );
    }
    case 'repeat':
      return block(BLOCK.repeat, s.id, undefined, {
        TIMES: value(s.times),
        DO: chain(s.body, value),
      });
    case 'while':
      return block(BLOCK.while, s.id, undefined, { COND: value(s.cond), DO: chain(s.body, value) });
    case 'for':
      return block(
        BLOCK.for,
        s.id,
        { VAR: { id: varId(s.name) }, DIR: s.down ? 'DOWN' : 'UP' },
        { FROM: value(s.from), TO: value(s.to), DO: chain(s.body, value) },
      );
    case 'newArray': {
      const fields = {
        VAR: { id: varId(s.name) },
        ELEM: ELEM_FIELD[s.elem],
        BASE: s.base === 1 ? 'ONE' : 'ZERO',
      };
      const [rows, cols] = s.dims;
      return cols
        ? block(BLOCK.newTable, s.id, fields, { ROWS: value(rows), COLS: value(cols) })
        : block(BLOCK.newList, s.id, fields, { SIZE: value(rows) });
    }
    case 'setAt':
      return s.index2
        ? block(
            BLOCK.setAtTable,
            s.id,
            { VAR: { id: varId(s.name) } },
            {
              INDEX: value(s.index),
              INDEX2: value(s.index2),
              VALUE: value(s.value),
            },
          )
        : block(
            BLOCK.setAt,
            s.id,
            { VAR: { id: varId(s.name) } },
            {
              INDEX: value(s.index),
              VALUE: value(s.value),
            },
          );
    case 'break':
      return block(BLOCK.break, s.id);
  }
}

/** Blocks for a program, ready to load into the workspace. */
export function toWorkspace(program: Program): WorkspaceJson {
  const start = block(BLOCK.start, program.startId ?? 'start');
  const first = chain(program.body, (e) => expression(e, program.types));
  if (first) start.next = { block: first };
  return {
    blocks: {
      languageVersion: 0,
      blocks: [{ ...start, x: 40, y: 40, deletable: false } as BlockJson],
    },
    variables: program.variables.map((name) => ({ name, id: varId(name) })),
  };
}
